import { useEffect, useLayoutEffect, useRef, useState } from "react";

export const ADMIN_TEXT_FILTER_DEBOUNCE_MS = 400;

/**
 * Delays server-backed text filter values so list pages do not refetch on every
 * keystroke.
 */
export const useDebouncedValue = <TValue>(
  value: TValue,
  delayMs: number = ADMIN_TEXT_FILTER_DEBOUNCE_MS,
  resetKey?: unknown,
): TValue => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  const previousResetKeyRef = useRef(resetKey);
  const shouldFlushResetValue = !Object.is(
    previousResetKeyRef.current,
    resetKey,
  );

  if (shouldFlushResetValue) {
    previousResetKeyRef.current = resetKey;
  }

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedValue(value);
    }, delayMs);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [delayMs, value]);

  useLayoutEffect(() => {
    if (shouldFlushResetValue && !Object.is(debouncedValue, value)) {
      setDebouncedValue(value);
    }
  }, [debouncedValue, shouldFlushResetValue, value]);

  return shouldFlushResetValue ? value : debouncedValue;
};
